import { useEffect, useState } from 'react'
import { ArrowRight, Trash2, X } from 'lucide-react'
import { Button, IconButton, Input, Switch } from '@/components/ui'
import { RuleBuilder } from './RuleBuilder.tsx'
import type { ConditionNode, TemplateTransition, TemplateVariable } from './types.ts'

interface EdgeInspectorProps {
  transition: TemplateTransition
  variables: TemplateVariable[]
  fromTitle?: string
  toTitle?: string
  onUpdate: (patch: { label?: string | null; condition?: ConditionNode | null; isDefault?: boolean }) => void
  onDelete: () => void
  onClose: () => void
  saving?: boolean
}

export function EdgeInspector({
  transition,
  variables,
  fromTitle,
  toTitle,
  onUpdate,
  onDelete,
  onClose,
  saving,
}: EdgeInspectorProps) {
  const [label, setLabel] = useState(transition.label ?? '')
  const [condition, setCondition] = useState<ConditionNode | null>(transition.condition)
  const [isDefault, setIsDefault] = useState(transition.is_default === 1)

  useEffect(() => {
    setLabel(transition.label ?? '')
    setCondition(transition.condition)
    setIsDefault(transition.is_default === 1)
  }, [transition.id]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSave() {
    onUpdate({ label: label.trim() || null, condition, isDefault })
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 shrink-0">
        <span className="text-sm font-semibold text-gray-900 flex-1">Verbindung</span>
        <IconButton icon={<X />} aria-label="Schließen" variant="ghost" size="sm" onClick={onClose} />
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <div className="flex items-center gap-2 text-xs text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
          <span className="font-medium truncate">{fromTitle ?? 'Schritt'}</span>
          <ArrowRight className="w-3.5 h-3.5 shrink-0" />
          <span className="font-medium truncate">{toTitle ?? 'Schritt'}</span>
        </div>

        <Input
          label="Beschriftung (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="z.B. genehmigt"
        />

        <div className="flex items-center gap-3 py-1">
          <Switch
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            label="Standard-Pfad (sonst)"
          />
        </div>
        <p className="text-xs text-neutral-500 -mt-2">
          Greift bei einem XOR-Gateway, wenn keine andere Bedingung zutrifft.
        </p>

        <div className="rounded-lg border border-gray-200 p-3 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Bedingung
          </p>
          <p className="text-xs text-neutral-500">
            Ohne Bedingung wird die Verbindung immer verfolgt (sequenziell / parallel).
          </p>
          <RuleBuilder variables={variables} value={condition} onChange={setCondition} />
        </div>
      </div>

      <div className="shrink-0 px-4 py-3 border-t border-gray-200 flex justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<Trash2 className="w-4 h-4" />}
          onClick={onDelete}
        >
          Löschen
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={saving}>
            Speichern
          </Button>
        </div>
      </div>
    </div>
  )
}
